/**
 * Pure reducer for document state management.
 *
 * All state updates flow through this reducer, enabling:
 * - Timestamp-based conflict resolution
 * - Predictable state transitions
 * - Easy testing
 */

import {
  DocumentsState,
  DocumentState,
  DocumentAction,
  ApiDocument,
  PipelineStep,
  PIPELINE_STEPS,
  createEmptyDocumentState,
  initialDocumentsState,
  HighlightedFact,
} from './types';

// NOTE: Step display names are now provided by the worker via processing_status
// in SSE events. This ensures single source of truth for step messages.
// The worker's STEP_MESSAGES in constants.py is the authoritative source.

/**
 * Parse highlighted_facts JSON string from API.
 * Returns null if parsing fails or value is null/undefined.
 * Format: [{"label": "Schlag", "value": "1570", "style": "default"}, ...]
 */
function parseHighlightedFacts(json: string | null | undefined): HighlightedFact[] | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as HighlightedFact[];
  } catch (e) {
    console.warn('[Reducer] Failed to parse highlighted_facts:', e);
    return null;
  }
}

function parseTags(json: string | null | undefined): string[] | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as string[];
  } catch (e) {
    console.warn('[Reducer] Failed to parse tags:', e);
    return null;
  }
}

/**
 * Convert API document to internal state format.
 * API is authoritative for completed documents.
 */
function apiDocumentToState(doc: ApiDocument, timestamp: number): DocumentState {
  return {
    docId: doc.doc_id,
    jobId: doc.job_id,
    filename: doc.filename,
    smartFilename: doc.smart_filename ?? null,
    documentType: doc.document_type,
    documentDate: doc.document_date,
    description: doc.description,
    // #1965 B2b: the dedicated split-parent marker (UDB 0051). The legacy
    // `document_type === 'multi-document'` sentinel dual-read was removed with
    // the classifier-v1 decommission (#2009) — the marker is authoritative.
    isMultiDocument: Boolean(doc.is_multi_document),
    parentSource: doc.parent_source
      ? {
          parentDocId: doc.parent_source.parent_doc_id,
          parentFilename: doc.parent_source.parent_filename,
          pageRange: doc.parent_source.page_range,
          pageRangeDisplay: doc.parent_source.page_range_display,
        }
      : null,
    uploadedAt: doc.uploaded_at,
    // Taxonomy-based classification (from INDEX.yaml)
    category: doc.category ?? null,
    subcategory: doc.subcategory ?? null,
    tier: doc.tier ?? null,
    // Icon from feedDisplay.icon in document type YAML
    icon: doc.icon ?? null,
    // Display name from document type YAML
    documentTypeDisplayName: doc.document_type_display_name ?? null,
    // Taxonomy-v2 native cell (#1373/#1454) — `roleName` drives the feed title
    domain: doc.domain ?? null,
    role: doc.role ?? null,
    domainName: doc.domain_name ?? null,
    roleName: doc.role_name ?? null,
    // Pre-computed highlight bubbles for folded view
    highlightedFacts: parseHighlightedFacts(doc.highlighted_facts),
    // User-defined tags
    tagIds: doc.tags ? parseTags(doc.tags) : null,
    status: doc.status === 'processing' ? 'processing' : doc.status,
    currentStep: null, // Will be filled by SSE or job status poll
    stepStatus: null,
    errorMessage: doc.error_message || null,
    // Human-readable processing status from parquet (survives page refresh)
    processingStatus: doc.processing_status || null,
    statusUpdatedAt: timestamp,
    stepUpdatedAt: timestamp,
    source: 'api',
  };
}

/**
 * Merge existing state with API data.
 * Preserves real-time SSE/poll step updates for processing documents.
 */
function mergeWithApiDocument(
  existing: DocumentState,
  apiDoc: ApiDocument,
  timestamp: number
): DocumentState {
  const apiState = apiDocumentToState(apiDoc, timestamp);

  // API is authoritative for completed/failed/filtered status.
  // ``filtered`` (issue #920) is terminal like the other two — no
  // further step info is going to arrive.
  if (
    apiDoc.status === 'completed' ||
    apiDoc.status === 'failed' ||
    apiDoc.status === 'filtered'
  ) {
    return {
      ...apiState,
      currentStep: null,
      stepStatus: null,
    };
  }

  // For processing documents, ALWAYS preserve existing step info from SSE/poll
  // because the API doesn't return step info - only SSE and poll provide it
  if (existing.currentStep !== null) {
    return {
      ...apiState,
      currentStep: existing.currentStep,
      stepStatus: existing.stepStatus,
      stepUpdatedAt: existing.stepUpdatedAt,
    };
  }

  return apiState;
}

/**
 * Main reducer function.
 * Handles all document state transitions.
 */
export function documentsReducer(
  state: DocumentsState,
  action: DocumentAction
): DocumentsState {
  switch (action.type) {
    // ==========================================
    // API Fetch Actions
    // ==========================================

    case 'FETCH_START': {
      return {
        ...state,
        loading: true,
        error: null,
      };
    }

    case 'FETCH_SUCCESS': {
      // Clear existing documents and replace with new page data
      // This prevents duplicates when changing pages
      const newDocuments = new Map<string, DocumentState>();
      const timestamp = action.timestamp;

      // FIRST: Preserve local documents that are still uploading or just uploaded
      // These won't be in the API response yet, so we need to keep them
      for (const [docId, doc] of state.documents.entries()) {
        if (doc.source === 'upload' &&
            (doc.status === 'pending' && (timestamp - doc.statusUpdatedAt) < 30000)) {
          // Keep recently uploaded documents (within 30s) that haven't appeared in API yet
          newDocuments.set(docId, doc);
        }
      }

      // THEN: Process documents from API (may override local ones if they exist in API)
      for (const apiDoc of action.documents) {
        const existing = state.documents.get(apiDoc.doc_id);

        if (existing) {
          // Merge with existing state, respecting timestamps (preserves SSE step info)
          newDocuments.set(apiDoc.doc_id, mergeWithApiDocument(existing, apiDoc, timestamp));
        } else {
          // New document from API
          newDocuments.set(apiDoc.doc_id, apiDocumentToState(apiDoc, timestamp));
        }
      }

      return {
        ...state,
        documents: newDocuments,
        loading: false,
        error: null,
        lastFetchedAt: timestamp,
      };
    }

    case 'FETCH_ERROR': {
      return {
        ...state,
        loading: false,
        error: action.error,
      };
    }

    // ==========================================
    // Upload Actions
    // ==========================================

    case 'UPLOAD_STARTED': {
      const newDocuments = new Map(state.documents);
      const existing = newDocuments.get(action.docId);

      if (existing) {
        // SSE may have already created a placeholder - merge
        newDocuments.set(action.docId, {
          ...existing,
          jobId: action.jobId,
          filename: action.filename,
          status: 'pending',
          statusUpdatedAt: Math.max(existing.statusUpdatedAt, action.timestamp),
          source: 'upload',
        });
      } else {
        // Create new document state
        const newDoc = createEmptyDocumentState(
          action.docId,
          action.filename,
          action.timestamp,
          'upload'
        );
        newDoc.jobId = action.jobId;
        newDoc.status = 'pending';
        newDocuments.set(action.docId, newDoc);
      }

      return {
        ...state,
        documents: newDocuments,
      };
    }

    // ==========================================
    // SSE Connection Actions
    // ==========================================

    case 'SSE_CONNECTED': {
      return {
        ...state,
        sseConnected: true,
      };
    }

    case 'SSE_DISCONNECTED': {
      return {
        ...state,
        sseConnected: false,
      };
    }

    // ==========================================
    // SSE Event Actions
    // ==========================================

    case 'SSE_STEP_QUEUED': {
      // Ignore step_queued events - we only care about step_started from worker
      // This keeps the UI simple: clock until worker picks up, then spinner
      // The current step stays visible between transitions (no flashing)
      return state;
    }

    case 'SSE_STEP_STARTED': {
      const newDocuments = new Map(state.documents);
      const existing = newDocuments.get(action.docId);

      const step = action.step as PipelineStep;
      if (!PIPELINE_STEPS.includes(step)) {
        console.warn(`[Reducer] Unknown pipeline step: ${action.step}`);
        return state;
      }

      // Use processing_status from worker (single source of truth)
      // Falls back to step name if not provided (shouldn't happen with updated worker)
      const processingStatus = action.processingStatus || step;

      if (existing) {
        if (action.timestamp >= existing.stepUpdatedAt) {
          newDocuments.set(action.docId, {
            ...existing,
            status: 'processing',
            currentStep: step,
            stepStatus: 'processing',
            processingStatus,
            statusUpdatedAt: Math.max(existing.statusUpdatedAt, action.timestamp),
            stepUpdatedAt: action.timestamp,
            source: 'sse',
          });
        }
      } else {
        const placeholder = createEmptyDocumentState(
          action.docId,
          'Processing...',
          action.timestamp,
          'sse'
        );
        placeholder.currentStep = step;
        placeholder.stepStatus = 'processing';
        placeholder.processingStatus = processingStatus;
        newDocuments.set(action.docId, placeholder);
      }

      return {
        ...state,
        documents: newDocuments,
      };
    }

    case 'SSE_COMPLETED': {
      const newDocuments = new Map(state.documents);
      const existing = newDocuments.get(action.docId);

      if (existing) {
        if (action.timestamp >= existing.statusUpdatedAt) {
          newDocuments.set(action.docId, {
            ...existing,
            status: 'completed',
            currentStep: null,
            stepStatus: null,
            errorMessage: null,
            processingStatus: null, // Clear on completion
            statusUpdatedAt: action.timestamp,
            stepUpdatedAt: action.timestamp,
            source: 'sse',
          });
        }
      } else {
        // Rare case - completed event without prior knowledge
        const placeholder = createEmptyDocumentState(
          action.docId,
          'Completed',
          action.timestamp,
          'sse'
        );
        placeholder.status = 'completed';
        placeholder.processingStatus = null;
        newDocuments.set(action.docId, placeholder);
      }

      return {
        ...state,
        documents: newDocuments,
      };
    }

    case 'SSE_FAILED': {
      const newDocuments = new Map(state.documents);
      const existing = newDocuments.get(action.docId);

      if (existing) {
        if (action.timestamp >= existing.statusUpdatedAt) {
          newDocuments.set(action.docId, {
            ...existing,
            status: 'failed',
            currentStep: null,
            stepStatus: null,
            errorMessage: action.error,
            processingStatus: `Error: ${action.error}`, // Show error in processing status
            statusUpdatedAt: action.timestamp,
            stepUpdatedAt: action.timestamp,
            source: 'sse',
          });
        }
      } else {
        const placeholder = createEmptyDocumentState(
          action.docId,
          'Failed',
          action.timestamp,
          'sse'
        );
        placeholder.processingStatus = `Error: ${action.error}`;
        placeholder.status = 'failed';
        placeholder.errorMessage = action.error;
        newDocuments.set(action.docId, placeholder);
      }

      return {
        ...state,
        documents: newDocuments,
      };
    }

    case 'SSE_DOCUMENT_CREATED': {
      const newDocuments = new Map(state.documents);
      const existing = newDocuments.get(action.docId);

      if (!existing) {
        // Create new child document placeholder
        const child = createEmptyDocumentState(
          action.docId,
          action.filename,
          action.timestamp,
          'sse'
        );

        // Link to parent if this is a split document
        if (action.parentDocId) {
          const parent = newDocuments.get(action.parentDocId);
          if (parent) {
            child.parentSource = {
              parentDocId: action.parentDocId,
              parentFilename: parent.filename,
              pageRange: '',
              pageRangeDisplay: '',
            };
          }
        }

        newDocuments.set(action.docId, child);
      } else {
        // Update existing with filename if we only had a placeholder
        if (existing.filename === 'Processing...' || existing.filename === 'Completed' || existing.filename === 'Failed') {
          newDocuments.set(action.docId, {
            ...existing,
            filename: action.filename,
          });
        }
      }

      return {
        ...state,
        documents: newDocuments,
      };
    }

    // ==========================================
    // Polling Actions (fallback)
    // ==========================================

    case 'POLL_JOB_STATUS': {
      const newDocuments = new Map(state.documents);
      const existing = newDocuments.get(action.docId);
      const jobStatus = action.status;

      // Determine current step from job status
      // Only set currentStep if the job has actually started processing a step
      let currentStep: PipelineStep | null = null;
      let stepStatus: 'queued' | 'processing' | null = null;

      if (jobStatus.status === 'processing' || jobStatus.status === 'pending') {
        // For pending jobs or jobs with step_status 'queued', don't set currentStep
        // This ensures isWaiting=true in the selector, showing clock icon instead of spinner
        const isStillQueued = jobStatus.status === 'pending' ||
          jobStatus.progress.step_status === 'queued';

        if (jobStatus.progress.current_step) {
          // Backend explicitly told us the current step
          currentStep = jobStatus.progress.current_step as PipelineStep;
        } else if (!isStillQueued && jobStatus.progress.steps_completed > 0) {
          // Only derive step from steps_completed if job is actually processing
          // and has completed at least one step (meaning it's not just starting)
          const stepsCompleted = jobStatus.progress.steps_completed;
          const stepIndex = Math.min(stepsCompleted, PIPELINE_STEPS.length - 1);
          currentStep = PIPELINE_STEPS[stepIndex];
        }
        // Note: If isStillQueued and no current_step, currentStep stays null
        // This makes isWaiting=true in selector, showing clock icon

        stepStatus = isStillQueued ? 'queued' : (jobStatus.progress.step_status || 'processing');
      }

      if (existing) {
        // Only update if this is newer than what we have
        if (action.timestamp >= existing.stepUpdatedAt) {
          newDocuments.set(action.docId, {
            ...existing,
            jobId: jobStatus.job_id,
            status: jobStatus.status,
            currentStep,
            stepStatus,
            errorMessage: jobStatus.error_message,
            statusUpdatedAt: action.timestamp,
            stepUpdatedAt: action.timestamp,
            source: 'poll',
          });
        }
      } else {
        // Create from poll data
        const doc = createEmptyDocumentState(
          action.docId,
          'Processing...',
          action.timestamp,
          'poll'
        );
        doc.jobId = jobStatus.job_id;
        doc.status = jobStatus.status;
        doc.currentStep = currentStep;
        doc.stepStatus = stepStatus;
        doc.errorMessage = jobStatus.error_message;
        newDocuments.set(action.docId, doc);
      }

      return {
        ...state,
        documents: newDocuments,
      };
    }

    // ==========================================
    // Optimistic UI Actions
    // ==========================================

    case 'OPTIMISTIC_TAG_UPDATE': {
      const newDocuments = new Map(state.documents);
      const existing = newDocuments.get(action.docId);

      if (existing) {
        newDocuments.set(action.docId, {
          ...existing,
          tagIds: action.tagIds,
        });
      }

      return {
        ...state,
        documents: newDocuments,
      };
    }

    default: {
      // TypeScript exhaustiveness check
      const _exhaustive: never = action;
      console.warn('[Reducer] Unknown action:', _exhaustive);
      return state;
    }
  }
}

/**
 * Create initial state.
 * Can be used for testing or reset.
 */
export function createInitialState(): DocumentsState {
  return { ...initialDocumentsState };
}
